import { Injectable } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

export interface SEOData {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  author?: string;
  canonical?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private defaultTitle = 'Lama Driving School | Certified Driving Lessons Staten Island NY';
  private defaultDescription = 'Learn to drive with Staten Island\'s top-rated driving school. Certified instructors, flexible scheduling, road test prep & 5-hour courses. Call (718) 799-1350.';
  private defaultKeywords = 'driving school Staten Island, driving lessons Staten Island, road test prep, 5-hour course, DMV test preparation';
  private defaultImage = 'https://lamadrivingschool.com/assets/img/driving-school-hero.jpg';
  private baseUrl = 'https://lamadrivingschool.com';

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private router: Router
  ) {
    // Update canonical URL on route changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.updateCanonicalUrl(event.urlAfterRedirects);
    });
  }

  /**
   * Update all SEO meta tags for a page
   */
  updateSEO(data: SEOData): void {
    // Update title
    const title = data.title || this.defaultTitle;
    this.titleService.setTitle(title);

    // Update meta description
    const description = data.description || this.defaultDescription;
    this.metaService.updateTag({ name: 'description', content: description });

    // Update keywords
    const keywords = data.keywords || this.defaultKeywords;
    this.metaService.updateTag({ name: 'keywords', content: keywords });

    // Update canonical URL
    const canonical = data.canonical || data.url || this.router.url;
    this.updateCanonicalUrl(canonical);

    // Update Open Graph tags
    this.updateOpenGraphTags({
      title,
      description,
      image: data.image || this.defaultImage,
      url: data.url || this.router.url,
      type: data.type || 'website'
    });

    // Update Twitter Card tags
    this.updateTwitterCardTags({
      title,
      description,
      image: data.image || this.defaultImage
    });

    // Update author if provided
    if (data.author) {
      this.metaService.updateTag({ name: 'author', content: data.author });
    }
  }

  /**
   * Update canonical URL
   */
  private updateCanonicalUrl(url: string): void {
    // Remove existing canonical link
    const existingLink = document.querySelector('link[rel="canonical"]');
    if (existingLink) {
      existingLink.remove();
    }

    // Add new canonical link
    const link: HTMLLinkElement = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    
    // Ensure URL is absolute
    const canonicalUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    link.setAttribute('href', canonicalUrl);
    
    document.head.appendChild(link);
  }

  /**
   * Update Open Graph meta tags
   */
  private updateOpenGraphTags(data: { title: string; description: string; image: string; url: string; type: string }): void {
    this.metaService.updateTag({ property: 'og:title', content: data.title });
    this.metaService.updateTag({ property: 'og:description', content: data.description });
    this.metaService.updateTag({ property: 'og:image', content: data.image });
    this.metaService.updateTag({ property: 'og:image:width', content: '1200' });
    this.metaService.updateTag({ property: 'og:image:height', content: '630' });
    this.metaService.updateTag({ property: 'og:url', content: `${this.baseUrl}${data.url}` });
    this.metaService.updateTag({ property: 'og:type', content: data.type });
    this.metaService.updateTag({ property: 'og:site_name', content: 'Lama Driving School' });
  }

  /**
   * Update Twitter Card meta tags
   */
  private updateTwitterCardTags(data: { title: string; description: string; image: string }): void {
    this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.metaService.updateTag({ name: 'twitter:title', content: data.title });
    this.metaService.updateTag({ name: 'twitter:description', content: data.description });
    this.metaService.updateTag({ name: 'twitter:image', content: data.image });
  }

  /**
   * Add JSON-LD structured data to the page
   */
  addStructuredData(data: any): void {
    // Remove existing structured data script
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
  }

  /**
   * Get DrivingSchool schema for homepage
   */
  getDrivingSchoolSchema(): any {
    return {
      "@context": "https://schema.org",
      "@type": "DrivingSchool",
      "name": "Lama Driving School",
      "url": "https://lamadrivingschool.com",
      "telephone": "+17187991350",
      "email": "info@lamadrivingschool.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "1749 Victory Blvd",
        "addressLocality": "Staten Island",
        "addressRegion": "NY",
        "postalCode": "10314",
        "addressCountry": "US"
      },
      "openingHours": ["Mo-Fr 08:00-17:00", "Sa 08:00-19:00"],
      "priceRange": "$50-$645",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "200"
      },
      "image": "https://lamadrivingschool.com/assets/Logo-Blue%20BG.png",
      "description": "Professional driving school in Staten Island, NY offering certified driving lessons, road test preparation, and 5-hour pre-licensing courses."
    };
  }

  /**
   * Get LocalBusiness schema
   */
  getLocalBusinessSchema(): any {
    return {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Lama Driving School",
      "image": "https://lamadrivingschool.com/assets/Logo-Blue%20BG.png",
      "@id": "https://lamadrivingschool.com",
      "url": "https://lamadrivingschool.com",
      "telephone": "+17187991350",
      "priceRange": "$50-$645",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "1749 Victory Blvd",
        "addressLocality": "Staten Island",
        "addressRegion": "NY",
        "postalCode": "10314",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 40.6282,
        "longitude": -74.1431
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "08:00",
          "closes": "17:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Saturday",
          "opens": "08:00",
          "closes": "19:00"
        }
      ]
    };
  }

  /**
   * Get Course schema
   */
  getCourseSchema(course: any): any {
    return {
      "@context": "https://schema.org",
      "@type": "Course",
      "name": course.name,
      "description": course.details,
      "provider": {
        "@type": "Organization",
        "name": "Lama Driving School",
        "sameAs": "https://lamadrivingschool.com"
      },
      "offers": {
        "@type": "Offer",
        "price": course.price,
        "priceCurrency": "USD"
      }
    };
  }

  /**
   * Get Product/Offer schema for packages
   */
  getProductSchema(pkg: any): any {
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": pkg.name,
      "description": pkg.details,
      "image": pkg.img1,
      "offers": {
        "@type": "Offer",
        "price": pkg.price,
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "Lama Driving School"
        }
      }
    };
  }

  /**
   * Get BreadcrumbList schema
   */
  getBreadcrumbSchema(items: Array<{ name: string; url: string }>): any {
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": `${this.baseUrl}${item.url}`
      }))
    };
  }

  /**
   * Set default SEO for homepage
   */
  setHomepageSEO(): void {
    this.updateSEO({
      title: 'Lama Driving School | Certified Driving Lessons Staten Island NY',
      description: 'Learn to drive with Staten Island\'s top-rated driving school. Certified instructors, flexible scheduling, road test prep & 5-hour courses. Call (718) 799-1350.',
      keywords: 'driving school Staten Island, driving lessons Staten Island, road test prep, 5-hour course, DMV test preparation',
      url: '/',
      canonical: 'https://lamadrivingschool.com/'
    });

    // Add structured data
    this.addStructuredData(this.getDrivingSchoolSchema());
  }
}
